/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { KeyboardEvent, MouseEvent } from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { WorkflowItem } from "@/api/models"

import { ChevronDown, Loader2, Play, Plus, Search, Trash2, Workflow, X } from "@/components/icons"
import { SidebarNavSection } from "@/components/sidebar/sidebar-nav-section"
import { SidebarSearchResultItem } from "@/components/sidebar/sidebar-search-result-item"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/use-sidebar"
import { useDeleteWorkflow } from "@/features/ai-explorer/hooks/use-delete-workflow"
import { useGetSharedWorkflows } from "@/features/ai-explorer/hooks/use-get-shared-workflows"
import { useGetWorkflows } from "@/features/ai-explorer/hooks/use-get-workflows"
import { cn } from "@/lib/utils"
import { usePendingWorkflowStore } from "@/stores/pending-workflow-store"
import { useWorkflowRunStore } from "@/stores/workflow-run-store"

import { WorkflowRunDialog } from "./workflow-run-dialog"
import { WorkflowSidebarItem } from "./workflow-sidebar-item"
import {
  canBulkDeleteWorkflow,
  getBulkDeletableWorkflows,
  mergeOwnedAndSharedWorkflows,
  takeVisibleWorkflowsByPriority,
} from "../utils/workflow-sidebar-utils"

const DEFAULT_VISIBLE_COUNT = 5

const FAVORITES_SECTION_OPEN_KEY = "sidebar-workflows-favorites-open"
const COMPANY_SECTION_OPEN_KEY = "sidebar-workflows-company-open"
const PERSONAL_SECTION_OPEN_KEY = "sidebar-workflows-personal-open"
const SHARED_SECTION_OPEN_KEY = "sidebar-workflows-shared-open"

type WorkflowsFolderProps = {
  className?: string
  currentWorkflowId?: string
  defaultExpanded?: boolean
  globalSearchTerm?: string
  showDeleteAction?: boolean
  stickyShowLess?: boolean
  visibleCount?: number
}

export function WorkflowsFolder({
  className,
  currentWorkflowId,
  defaultExpanded = true,
  globalSearchTerm = "",
  showDeleteAction = false,
  stickyShowLess = false,
  visibleCount = DEFAULT_VISIBLE_COUNT,
}: WorkflowsFolderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { state: sidebarState, isMobile } = useSidebar()
  const isCollapsedSidebar = sidebarState === "collapsed" && !isMobile
  const setPendingWorkflow = usePendingWorkflowStore((state) => state.setPendingWorkflow)
  const runningWorkflowId = useWorkflowRunStore((state) => state.runningWorkflowId)
  const isOnExplorerPage = location.pathname.includes("/explorer")

  const { data: workflowsData, isPending: isLoadingOwnedWorkflows } = useGetWorkflows()
  const { data: sharedWorkflowsData, isPending: isLoadingSharedWorkflows } = useGetSharedWorkflows()
  const workflows = useMemo(
    () =>
      mergeOwnedAndSharedWorkflows(
        workflowsData?.workflows ?? [],
        sharedWorkflowsData?.workflows ?? []
      ),
    [sharedWorkflowsData?.workflows, workflowsData?.workflows]
  )
  const isLoadingWorkflows = isLoadingOwnedWorkflows || isLoadingSharedWorkflows

  const [localSearchTerm, setLocalSearchTerm] = useState("")
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showAllItems, setShowAllItems] = useState(false)
  const [isCollapsedPopoverOpen, setIsCollapsedPopoverOpen] = useState(false)
  const [showAllInCollapsed, setShowAllInCollapsed] = useState(false)
  const [workflowToRun, setWorkflowToRun] = useState<WorkflowItem | null>(null)
  const deleteWorkflowMutation = useDeleteWorkflow()
  const [selectedWorkflows, setSelectedWorkflows] = useState<WorkflowItem[]>([])
  const [selectMode, setSelectMode] = useState(false)
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null)
  const [showDeleteWorkflowsDialog, setShowDeleteWorkflowsDialog] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)

  const effectiveSearchTerm = globalSearchTerm || localSearchTerm
  const isSearchingWorkflows = Boolean(effectiveSearchTerm)
  const previousSearchTermRef = useRef(effectiveSearchTerm)

  useEffect(() => {
    if (!showDeleteAction && selectMode) {
      setSelectMode(false)
      setSelectedWorkflows([])
      setLastSelectedIndex(null)
    }
  }, [selectMode, showDeleteAction])

  useEffect(() => {
    if (!selectMode) {
      previousSearchTermRef.current = effectiveSearchTerm
      return
    }

    if (previousSearchTermRef.current !== effectiveSearchTerm) {
      setSelectedWorkflows([])
      setLastSelectedIndex(null)
      previousSearchTermRef.current = effectiveSearchTerm
    }
  }, [effectiveSearchTerm, selectMode])

  const [isFavoritesSectionOpenStored, setIsFavoritesSectionOpenStored] = useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(FAVORITES_SECTION_OPEN_KEY)
    return stored !== null ? stored === "true" : true
  })

  const [isCompanySectionOpenStored, setIsCompanySectionOpenStored] = useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(COMPANY_SECTION_OPEN_KEY)
    return stored !== null ? stored === "true" : true
  })

  const [isPersonalSectionOpenStored, setIsPersonalSectionOpenStored] = useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(PERSONAL_SECTION_OPEN_KEY)
    return stored !== null ? stored === "true" : true
  })

  const [isSharedSectionOpenStored, setIsSharedSectionOpenStored] = useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(SHARED_SECTION_OPEN_KEY)
    return stored !== null ? stored === "true" : true
  })

  const setIsFavoritesSectionOpen = useCallback((open: boolean) => {
    setIsFavoritesSectionOpenStored(open)
    if (typeof window !== "undefined") {
      localStorage.setItem(FAVORITES_SECTION_OPEN_KEY, String(open))
    }
  }, [])

  const setIsCompanySectionOpen = useCallback((open: boolean) => {
    setIsCompanySectionOpenStored(open)
    if (typeof window !== "undefined") {
      localStorage.setItem(COMPANY_SECTION_OPEN_KEY, String(open))
    }
  }, [])

  const setIsPersonalSectionOpen = useCallback((open: boolean) => {
    setIsPersonalSectionOpenStored(open)
    if (typeof window !== "undefined") {
      localStorage.setItem(PERSONAL_SECTION_OPEN_KEY, String(open))
    }
  }, [])

  const setIsSharedSectionOpen = useCallback((open: boolean) => {
    setIsSharedSectionOpenStored(open)
    if (typeof window !== "undefined") {
      localStorage.setItem(SHARED_SECTION_OPEN_KEY, String(open))
    }
  }, [])

  const isFavoritesSectionOpen = isSearchingWorkflows ? true : isFavoritesSectionOpenStored
  const isCompanySectionOpen = isSearchingWorkflows ? true : isCompanySectionOpenStored
  const isPersonalSectionOpen = isSearchingWorkflows ? true : isPersonalSectionOpenStored
  const isSharedSectionOpen = isSearchingWorkflows ? true : isSharedSectionOpenStored

  const filteredWorkflows = useMemo(() => {
    if (!effectiveSearchTerm) return workflows
    return workflows.filter(
      (wf) =>
        wf.title.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        wf.category?.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        wf.tags?.some((tag) => tag.toLowerCase().includes(effectiveSearchTerm.toLowerCase()))
    )
  }, [workflows, effectiveSearchTerm])

  const favoriteWorkflows = useMemo(
    () => filteredWorkflows.filter((wf) => wf.is_favorite && wf.visibility !== "shared"),
    [filteredWorkflows]
  )

  const nonFavoriteWorkflows = useMemo(
    () => filteredWorkflows.filter((wf) => !wf.is_favorite || wf.visibility === "shared"),
    [filteredWorkflows]
  )

  const companyWorkflows = useMemo(
    () => nonFavoriteWorkflows.filter((wf) => wf.visibility === "company"),
    [nonFavoriteWorkflows]
  )

  const personalWorkflows = useMemo(
    () => nonFavoriteWorkflows.filter((wf) => wf.visibility === "personal"),
    [nonFavoriteWorkflows]
  )

  const sharedWorkflows = useMemo(
    () => nonFavoriteWorkflows.filter((wf) => wf.visibility === "shared"),
    [nonFavoriteWorkflows]
  )

  const {
    visibleFavoriteWorkflows,
    visibleCompanyWorkflows,
    visiblePersonalWorkflows,
    visibleSharedWorkflows,
  } = useMemo(() => {
    if (showAllItems || effectiveSearchTerm) {
      return {
        visibleFavoriteWorkflows: favoriteWorkflows,
        visibleCompanyWorkflows: companyWorkflows,
        visiblePersonalWorkflows: personalWorkflows,
        visibleSharedWorkflows: sharedWorkflows,
      }
    }
    const visibleWorkflows = takeVisibleWorkflowsByPriority(visibleCount, {
      favorites: favoriteWorkflows,
      company: companyWorkflows,
      personal: personalWorkflows,
      shared: sharedWorkflows,
    })
    return {
      visibleFavoriteWorkflows: visibleWorkflows.favorites,
      visibleCompanyWorkflows: visibleWorkflows.company,
      visiblePersonalWorkflows: visibleWorkflows.personal,
      visibleSharedWorkflows: visibleWorkflows.shared,
    }
  }, [
    favoriteWorkflows,
    companyWorkflows,
    personalWorkflows,
    sharedWorkflows,
    showAllItems,
    effectiveSearchTerm,
    visibleCount,
  ])

  const allVisibleWorkflows = useMemo(
    () => [
      ...visibleFavoriteWorkflows,
      ...visibleCompanyWorkflows,
      ...visiblePersonalWorkflows,
      ...visibleSharedWorkflows,
    ],
    [
      visibleFavoriteWorkflows,
      visibleCompanyWorkflows,
      visiblePersonalWorkflows,
      visibleSharedWorkflows,
    ]
  )

  const workflowIndexMap = useMemo(() => {
    const map = new Map<string, number>()
    allVisibleWorkflows.forEach((workflow, index) => {
      map.set(workflow.id, index)
    })
    return map
  }, [allVisibleWorkflows])

  const bulkDeletableVisibleWorkflows = useMemo(
    () => getBulkDeletableWorkflows(allVisibleWorkflows),
    [allVisibleWorkflows]
  )

  const {
    collapsedFavoriteWorkflows,
    collapsedCompanyWorkflows,
    collapsedPersonalWorkflows,
    collapsedSharedWorkflows,
  } = useMemo(() => {
    if (effectiveSearchTerm || showAllInCollapsed) {
      return {
        collapsedFavoriteWorkflows: favoriteWorkflows,
        collapsedCompanyWorkflows: companyWorkflows,
        collapsedPersonalWorkflows: personalWorkflows,
        collapsedSharedWorkflows: sharedWorkflows,
      }
    }
    const visibleWorkflows = takeVisibleWorkflowsByPriority(visibleCount, {
      favorites: favoriteWorkflows,
      company: companyWorkflows,
      personal: personalWorkflows,
      shared: sharedWorkflows,
    })
    return {
      collapsedFavoriteWorkflows: visibleWorkflows.favorites,
      collapsedCompanyWorkflows: visibleWorkflows.company,
      collapsedPersonalWorkflows: visibleWorkflows.personal,
      collapsedSharedWorkflows: visibleWorkflows.shared,
    }
  }, [
    favoriteWorkflows,
    companyWorkflows,
    personalWorkflows,
    sharedWorkflows,
    effectiveSearchTerm,
    showAllInCollapsed,
    visibleCount,
  ])

  const totalVisibleInCollapsed =
    collapsedFavoriteWorkflows.length +
    collapsedCompanyWorkflows.length +
    collapsedPersonalWorkflows.length +
    collapsedSharedWorkflows.length
  const hasMoreWorkflows = filteredWorkflows.length > totalVisibleInCollapsed

  const handleWorkflowClick = useCallback(
    (workflowId: string) => {
      void navigate(`/flows/${workflowId}`)
    },
    [navigate]
  )

  const handleRunWorkflow = useCallback(
    (workflow: WorkflowItem) => {
      if (isOnExplorerPage) {
        setPendingWorkflow(workflow)
      } else {
        setWorkflowToRun(workflow)
      }
    },
    [isOnExplorerPage, setPendingWorkflow]
  )

  const handleCollapsedWorkflowClick = useCallback(
    (workflow: WorkflowItem) => {
      void navigate(`/flows/${workflow.id}`)
      setIsCollapsedPopoverOpen(false)
    },
    [navigate]
  )

  const handleCollapsedRunWorkflow = useCallback(
    (workflow: WorkflowItem) => {
      setIsCollapsedPopoverOpen(false)
      if (isOnExplorerPage) {
        setPendingWorkflow(workflow)
      } else {
        setWorkflowToRun(workflow)
      }
    },
    [isOnExplorerPage, setPendingWorkflow]
  )

  const handleNewWorkflow = useCallback(() => {
    void navigate("/flows")
  }, [navigate])
  const handleHeaderClick = useCallback(() => {
    void navigate("/flows")
  }, [navigate])

  const handleSelectWorkflow = useCallback(
    (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>, workflow: WorkflowItem) => {
      event.preventDefault()
      event.stopPropagation()
      if (!canBulkDeleteWorkflow(workflow)) return
      const workflowIndex = workflowIndexMap.get(workflow.id)
      if (workflowIndex === undefined) return

      if ("shiftKey" in event && event.shiftKey && lastSelectedIndex !== null) {
        const startIndex = Math.min(lastSelectedIndex, workflowIndex)
        const endIndex = Math.max(lastSelectedIndex, workflowIndex)
        setSelectedWorkflows(
          getBulkDeletableWorkflows(allVisibleWorkflows.slice(startIndex, endIndex + 1))
        )
        setLastSelectedIndex(workflowIndex)
        return
      }

      setSelectedWorkflows((prev) => {
        const isSelected = prev.some((selected) => selected.id === workflow.id)
        if (isSelected) {
          return prev.filter((selected) => selected.id !== workflow.id)
        }
        return [...prev, workflow]
      })
      setLastSelectedIndex(workflowIndex)
    },
    [allVisibleWorkflows, lastSelectedIndex, workflowIndexMap]
  )

  const handleSelectModeToggle = useCallback(
    (workflow: WorkflowItem) => {
      if (!canBulkDeleteWorkflow(workflow)) return
      setSelectMode(true)
      setSelectedWorkflows([workflow])
      const workflowIndex = workflowIndexMap.get(workflow.id)
      setLastSelectedIndex(workflowIndex ?? null)
    },
    [workflowIndexMap]
  )

  const handleExitSelectMode = useCallback(() => {
    setSelectMode(false)
    setSelectedWorkflows([])
    setLastSelectedIndex(null)
    setShowDeleteWorkflowsDialog(false)
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    if (selectedWorkflows.length === bulkDeletableVisibleWorkflows.length) {
      setSelectedWorkflows([])
      setLastSelectedIndex(null)
      return
    }
    setSelectedWorkflows(bulkDeletableVisibleWorkflows)
    setLastSelectedIndex(allVisibleWorkflows.length - 1)
  }, [allVisibleWorkflows.length, bulkDeletableVisibleWorkflows, selectedWorkflows.length])

  const handleConfirmDeleteWorkflows = async () => {
    if (selectedWorkflows.length === 0) return

    setIsBulkDeleting(true)
    const results = await Promise.allSettled(
      selectedWorkflows.map((workflow) => deleteWorkflowMutation.mutateAsync(workflow.id))
    )
    setIsBulkDeleting(false)
    setShowDeleteWorkflowsDialog(false)

    const hasErrors = results.some((result) => result.status === "rejected")
    if (hasErrors) {
      toast.error(t("workflows.deleteManyError", { defaultValue: "Failed to delete flows" }))
    } else {
      toast.success(t("workflows.deletedMany", { defaultValue: "Flows deleted" }))
    }

    if (selectedWorkflows.some((workflow) => workflow.id === currentWorkflowId)) {
      void navigate("/flows")
    }

    setSelectedWorkflows([])
    setSelectMode(false)
    setLastSelectedIndex(null)
  }

  const shouldShowSelectModeHeader = showDeleteAction && selectMode

  const headerActions = (
    <>
      {!globalSearchTerm && (
        <Button
          aria-label={t("workflows.search", { defaultValue: "Search" })}
          size="icon"
          variant="ghost"
          className={cn(
            "hover:bg-foreground/15 size-7 rounded-full p-0",
            localSearchTerm && "bg-sidebar-accent text-sidebar-accent-foreground"
          )}
          onClick={(e) => {
            e.stopPropagation()
            setIsSearchOpen(true)
          }}
        >
          <Search className="size-4" />
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={t("workflowLanding.newWorkflow")}
            className="hover:bg-foreground/15 size-7 rounded-full p-0"
            size="icon"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              handleNewWorkflow()
            }}
          >
            <Plus className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={5}>
          <p>{t("workflowLanding.newWorkflow")}</p>
        </TooltipContent>
      </Tooltip>
    </>
  )

  const renderCollapsedItem = (workflow: WorkflowItem) => (
    <div key={workflow.id} className="group/collapsed-item relative">
      <SidebarSearchResultItem
        title={workflow.title}
        className={cn(
          "pr-14",
          currentWorkflowId === workflow.id && "bg-sidebar-accent text-sidebar-accent-foreground"
        )}
        icon={
          runningWorkflowId === workflow.id ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Workflow className="size-4" />
          )
        }
        onClick={() => handleCollapsedWorkflowClick(workflow)}
      />
      <div className="absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-0.5">
        <Button
          aria-label={t("questioninput.workflow_selection.run")}
          className="hover:bg-foreground/15 h-6 gap-1 rounded-full px-2.5 text-xs font-medium opacity-100 transition-opacity duration-150"
          disabled={runningWorkflowId === workflow.id}
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            handleCollapsedRunWorkflow(workflow)
          }}
        >
          {runningWorkflowId === workflow.id ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Play className="size-3 fill-current" />
          )}
          {t("questioninput.workflow_selection.run", { defaultValue: "Run" })}
        </Button>
      </div>
    </div>
  )

  const deleteDialogs = (
    <>
      <Dialog open={showDeleteWorkflowsDialog} onOpenChange={setShowDeleteWorkflowsDialog}>
        <DialogContent className="flex max-h-[90vh] min-h-0 min-w-[300px] flex-1 flex-col rounded-sm sm:max-w-[90vw] lg:max-w-[900px]">
          <DialogHeader className="flex size-full min-h-0 flex-1 flex-col gap-2">
            <DialogTitle>
              {t("workflows.confirmDeleteManyTitle", { defaultValue: "Delete Flows" })}
            </DialogTitle>
            <DialogDescription className="size-full max-h-[500px] overflow-y-auto">
              <p className="text-muted-foreground mb-3 text-sm">
                {t("workflows.confirmDeleteManyDescription", {
                  defaultValue:
                    "Are you sure you want to delete these flows? This action cannot be undone.",
                })}
              </p>
              <ol className="list-decimal space-y-3 pl-6">
                {selectedWorkflows.map((workflow) => (
                  <li
                    key={workflow.id}
                    className="border-border/50 bg-muted/40 flex flex-col space-y-1 rounded-md border px-3 py-2"
                  >
                    <span className="text-sm font-medium">{workflow.title}</span>
                  </li>
                ))}
              </ol>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="min-h-0">
            <Button
              disabled={isBulkDeleting || selectedWorkflows.length === 0}
              variant="destructive"
              onClick={handleConfirmDeleteWorkflows}
            >
              {t("workflowLanding.deleteDialog.delete")}
              {isBulkDeleting && <Spinner className="ml-2" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )

  if (isCollapsedSidebar) {
    return (
      <>
        <Popover
          open={isCollapsedPopoverOpen}
          onOpenChange={(open) => {
            setIsCollapsedPopoverOpen(open)
            if (!open) setShowAllInCollapsed(false)
          }}
        >
          <PopoverTrigger asChild>
            <button
              aria-label={t("workflowLanding.title")}
              type="button"
              className={cn(
                "group/nav-section hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex size-10 items-center justify-center rounded-md transition-colors duration-150",
                className
              )}
            >
              <Workflow className="size-6" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="flex w-72 flex-col p-0"
            side="right"
            sideOffset={8}
          >
            <div className="border-b p-2">
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                <Input
                  aria-label={t("workflowLanding.searchPlaceholder")}
                  className="placeholder:text-muted-foreground h-8 pl-9 text-sm"
                  placeholder={t("workflowLanding.searchPlaceholder")}
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && localSearchTerm && setLocalSearchTerm("")}
                />
              </div>
            </div>
            <div className="relative flex min-h-0 flex-1 flex-col">
              {showAllInCollapsed && (
                <div className="from-popover pointer-events-none absolute top-0 right-0 left-0 z-10 h-4 bg-gradient-to-b to-transparent" />
              )}
              <div
                className={cn(
                  "overflow-y-auto",
                  showAllInCollapsed ? "max-h-[300px] pb-10" : "max-h-[300px]"
                )}
              >
                <div className="flex flex-col gap-2 p-1">
                  {isLoadingWorkflows && (
                    <p className="animate-shimmer from-muted-foreground via-background to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text px-2 py-4 text-center text-xs text-transparent">
                      {t("workflows.loading", { defaultValue: "Loading flows..." })}
                    </p>
                  )}
                  {!isLoadingWorkflows && workflows.length === 0 && (
                    <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                      {t("workflowLanding.emptyState")}
                    </p>
                  )}
                  {!isLoadingWorkflows &&
                    workflows.length > 0 &&
                    collapsedFavoriteWorkflows.length === 0 &&
                    collapsedCompanyWorkflows.length === 0 &&
                    collapsedPersonalWorkflows.length === 0 &&
                    collapsedSharedWorkflows.length === 0 &&
                    effectiveSearchTerm && (
                      <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                        {t("workflows.noMatches", { defaultValue: "No flows found" })}
                      </p>
                    )}

                  {collapsedFavoriteWorkflows.length > 0 && (
                    <Collapsible
                      open={isFavoritesSectionOpen}
                      onOpenChange={setIsFavoritesSectionOpen}
                    >
                      <section className="flex flex-col gap-1">
                        <CollapsibleTrigger
                          className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                          disabled={isSearchingWorkflows}
                          type="button"
                        >
                          {t("workflowLanding.tabs.favorites", { defaultValue: "Favorites" })}
                          <ChevronDown
                            className={cn(
                              "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                              !isFavoritesSectionOpen && "-rotate-90"
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {collapsedFavoriteWorkflows.map(renderCollapsedItem)}
                        </CollapsibleContent>
                      </section>
                    </Collapsible>
                  )}

                  {collapsedCompanyWorkflows.length > 0 && (
                    <Collapsible open={isCompanySectionOpen} onOpenChange={setIsCompanySectionOpen}>
                      <section className="flex flex-col gap-1">
                        <CollapsibleTrigger
                          className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                          disabled={isSearchingWorkflows}
                          type="button"
                        >
                          {t("workflowLanding.tile.companyFlow")}
                          <ChevronDown
                            className={cn(
                              "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                              !isCompanySectionOpen && "-rotate-90"
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {collapsedCompanyWorkflows.map(renderCollapsedItem)}
                        </CollapsibleContent>
                      </section>
                    </Collapsible>
                  )}

                  {collapsedPersonalWorkflows.length > 0 && (
                    <Collapsible
                      open={isPersonalSectionOpen}
                      onOpenChange={setIsPersonalSectionOpen}
                    >
                      <section className="flex flex-col gap-1">
                        <CollapsibleTrigger
                          className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                          disabled={isSearchingWorkflows}
                          type="button"
                        >
                          {t("workflowLanding.tile.personal")}
                          <ChevronDown
                            className={cn(
                              "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                              !isPersonalSectionOpen && "-rotate-90"
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {collapsedPersonalWorkflows.map(renderCollapsedItem)}
                        </CollapsibleContent>
                      </section>
                    </Collapsible>
                  )}

                  {collapsedSharedWorkflows.length > 0 && (
                    <Collapsible open={isSharedSectionOpen} onOpenChange={setIsSharedSectionOpen}>
                      <section className="flex flex-col gap-1">
                        <CollapsibleTrigger
                          className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                          disabled={isSearchingWorkflows}
                          type="button"
                        >
                          {t("workflowLanding.tabs.shared", { defaultValue: "Shared" })}
                          <ChevronDown
                            className={cn(
                              "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                              !isSharedSectionOpen && "-rotate-90"
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {collapsedSharedWorkflows.map(renderCollapsedItem)}
                        </CollapsibleContent>
                      </section>
                    </Collapsible>
                  )}

                  {hasMoreWorkflows && !effectiveSearchTerm && !showAllInCollapsed && (
                    <Button
                      className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mt-1 h-7 w-full justify-start px-3 text-xs"
                      variant="ghost"
                      onClick={() => setShowAllInCollapsed(true)}
                    >
                      {`${t("sidebar.see_all", { defaultValue: "See all" })} (${filteredWorkflows.length})`}
                    </Button>
                  )}
                </div>
              </div>
              {showAllInCollapsed && !effectiveSearchTerm && (
                <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex flex-col">
                  <div className="from-popover h-6 bg-gradient-to-t to-transparent" />
                  <div className="bg-popover px-1 pb-1">
                    <Button
                      className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pointer-events-auto h-7 w-full justify-start px-3 text-xs"
                      variant="ghost"
                      onClick={() => setShowAllInCollapsed(false)}
                    >
                      {t("sidebar.show_less", { defaultValue: "Show less" })}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <WorkflowRunDialog
          open={!!workflowToRun}
          workflow={workflowToRun ?? undefined}
          workflowId={workflowToRun?.id}
          onOpenChange={(open) => !open && setWorkflowToRun(null)}
        />
        {deleteDialogs}
      </>
    )
  }

  return (
    <>
      <SidebarNavSection
        allItemsShown={showAllItems}
        className={className}
        defaultExpanded={defaultExpanded}
        forceExpanded={!!globalSearchTerm}
        headerActions={headerActions}
        icon={<Workflow className="size-4" />}
        isSearchActive={isSearchOpen && !globalSearchTerm}
        searchPlaceholder={t("workflowLanding.searchPlaceholder")}
        searchTerm={localSearchTerm}
        stickyShowLess={stickyShowLess}
        storageKey="sidebar-workflows-collapsed"
        title={t("workflowLanding.title")}
        totalCount={filteredWorkflows.length}
        visibleCount={visibleCount}
        onHeaderClick={handleHeaderClick}
        onSearchChange={setLocalSearchTerm}
        onSearchClose={() => setIsSearchOpen(false)}
        onSeeAllClick={() => setShowAllItems(true)}
        onShowLessClick={() => setShowAllItems(false)}
      >
        {isLoadingWorkflows && (
          <p className="animate-shimmer from-muted-foreground via-background to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text px-2 py-4 text-center text-xs text-transparent">
            {t("workflows.loading", { defaultValue: "Loading flows..." })}
          </p>
        )}
        {!isLoadingWorkflows &&
          visibleFavoriteWorkflows.length === 0 &&
          visibleCompanyWorkflows.length === 0 &&
          visiblePersonalWorkflows.length === 0 &&
          visibleSharedWorkflows.length === 0 && (
            <p className="text-muted-foreground px-2 py-4 text-center text-xs">
              {effectiveSearchTerm
                ? t("workflows.noMatches", { defaultValue: "No flows found" })
                : t("workflowLanding.emptyState")}
            </p>
          )}

        {shouldShowSelectModeHeader && (
          <div className="mb-2 flex items-center gap-1.5 px-2 text-xs">
            <span className="text-muted-foreground min-w-0 shrink-0 font-medium tabular-nums">
              {selectedWorkflows.length}
            </span>
            <Button
              className="h-6 min-w-0 shrink px-2 text-xs"
              disabled={bulkDeletableVisibleWorkflows.length === 0}
              size="sm"
              variant="outline"
              onClick={handleToggleSelectAll}
            >
              <span className="truncate">
                {selectedWorkflows.length === bulkDeletableVisibleWorkflows.length &&
                bulkDeletableVisibleWorkflows.length > 0
                  ? t("workflows.deselectAll", { defaultValue: "Deselect all" })
                  : t("workflows.selectAll", { defaultValue: "Select all" })}
              </span>
            </Button>
            {selectedWorkflows.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("workflows.deleteSelected", { defaultValue: "Delete selected" })}
                    className="text-destructive size-6 shrink-0 p-0"
                    size="icon"
                    variant="ghost"
                    onClick={() => setShowDeleteWorkflowsDialog(true)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {t("workflows.deleteSelected", { defaultValue: "Delete selected" })}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={t("common.cancel", { defaultValue: "Cancel" })}
                  className="size-6 shrink-0 p-0"
                  size="icon"
                  variant="ghost"
                  onClick={handleExitSelectMode}
                >
                  <X className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("common.cancel", { defaultValue: "Cancel" })}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {(visibleFavoriteWorkflows.length > 0 ||
          visibleCompanyWorkflows.length > 0 ||
          visiblePersonalWorkflows.length > 0 ||
          visibleSharedWorkflows.length > 0) && (
          <div className="flex flex-col gap-4">
            {visibleFavoriteWorkflows.length > 0 && (
              <Collapsible open={isFavoritesSectionOpen} onOpenChange={setIsFavoritesSectionOpen}>
                <section className="flex flex-col gap-1">
                  <CollapsibleTrigger
                    className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                    disabled={isSearchingWorkflows}
                    type="button"
                  >
                    {t("workflowLanding.tabs.favorites", { defaultValue: "Favorites" })}
                    <ChevronDown
                      className={cn(
                        "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                        !isFavoritesSectionOpen && "-rotate-90"
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="flex flex-col">
                      {visibleFavoriteWorkflows.map((workflow) => (
                        <WorkflowSidebarItem
                          key={workflow.id}
                          currentWorkflowId={currentWorkflowId}
                          isMobile={isMobile}
                          runningWorkflowId={runningWorkflowId}
                          selectMode={selectMode}
                          showDeleteAction={false}
                          workflow={workflow}
                          isSelected={selectedWorkflows.some(
                            (selected) => selected.id === workflow.id
                          )}
                          onClick={() => handleWorkflowClick(workflow.id)}
                          onRun={() => handleRunWorkflow(workflow)}
                          onSelect={handleSelectWorkflow}
                          onSelectModeToggle={handleSelectModeToggle}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </section>
              </Collapsible>
            )}
            {visibleCompanyWorkflows.length > 0 && (
              <Collapsible open={isCompanySectionOpen} onOpenChange={setIsCompanySectionOpen}>
                <section className="flex flex-col gap-1">
                  <CollapsibleTrigger
                    className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                    disabled={isSearchingWorkflows}
                    type="button"
                  >
                    {t("workflowLanding.tile.companyFlow")}
                    <ChevronDown
                      className={cn(
                        "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                        !isCompanySectionOpen && "-rotate-90"
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="flex flex-col">
                      {visibleCompanyWorkflows.map((workflow) => (
                        <WorkflowSidebarItem
                          key={workflow.id}
                          currentWorkflowId={currentWorkflowId}
                          isMobile={isMobile}
                          runningWorkflowId={runningWorkflowId}
                          selectMode={selectMode}
                          showDeleteAction={showDeleteAction}
                          workflow={workflow}
                          isSelected={selectedWorkflows.some(
                            (selected) => selected.id === workflow.id
                          )}
                          onClick={() => handleWorkflowClick(workflow.id)}
                          onRun={() => handleRunWorkflow(workflow)}
                          onSelect={handleSelectWorkflow}
                          onSelectModeToggle={handleSelectModeToggle}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </section>
              </Collapsible>
            )}

            {visiblePersonalWorkflows.length > 0 && (
              <Collapsible open={isPersonalSectionOpen} onOpenChange={setIsPersonalSectionOpen}>
                <section className="flex flex-col gap-1">
                  <CollapsibleTrigger
                    className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                    disabled={isSearchingWorkflows}
                    type="button"
                  >
                    {t("workflowLanding.tile.personal")}
                    <ChevronDown
                      className={cn(
                        "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                        !isPersonalSectionOpen && "-rotate-90"
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="flex flex-col">
                      {visiblePersonalWorkflows.map((workflow) => (
                        <WorkflowSidebarItem
                          key={workflow.id}
                          currentWorkflowId={currentWorkflowId}
                          isMobile={isMobile}
                          runningWorkflowId={runningWorkflowId}
                          selectMode={selectMode}
                          showDeleteAction={showDeleteAction}
                          workflow={workflow}
                          isSelected={selectedWorkflows.some(
                            (selected) => selected.id === workflow.id
                          )}
                          onClick={() => handleWorkflowClick(workflow.id)}
                          onRun={() => handleRunWorkflow(workflow)}
                          onSelect={handleSelectWorkflow}
                          onSelectModeToggle={handleSelectModeToggle}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </section>
              </Collapsible>
            )}

            {visibleSharedWorkflows.length > 0 && (
              <Collapsible open={isSharedSectionOpen} onOpenChange={setIsSharedSectionOpen}>
                <section className="flex flex-col gap-1">
                  <CollapsibleTrigger
                    className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                    disabled={isSearchingWorkflows}
                    type="button"
                  >
                    {t("workflowLanding.tabs.shared", { defaultValue: "Shared" })}
                    <ChevronDown
                      className={cn(
                        "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                        !isSharedSectionOpen && "-rotate-90"
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="flex flex-col">
                      {visibleSharedWorkflows.map((workflow) => (
                        <WorkflowSidebarItem
                          key={workflow.id}
                          currentWorkflowId={currentWorkflowId}
                          isMobile={isMobile}
                          runningWorkflowId={runningWorkflowId}
                          selectMode={selectMode}
                          showDeleteAction={showDeleteAction}
                          workflow={workflow}
                          isSelected={selectedWorkflows.some(
                            (selected) => selected.id === workflow.id
                          )}
                          onClick={() => handleWorkflowClick(workflow.id)}
                          onRun={() => handleRunWorkflow(workflow)}
                          onSelect={handleSelectWorkflow}
                          onSelectModeToggle={handleSelectModeToggle}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </section>
              </Collapsible>
            )}
          </div>
        )}
      </SidebarNavSection>
      <WorkflowRunDialog
        open={!!workflowToRun}
        workflow={workflowToRun ?? undefined}
        workflowId={workflowToRun?.id}
        onOpenChange={(open) => !open && setWorkflowToRun(null)}
      />
      {deleteDialogs}
    </>
  )
}
